import { BreakpointObserver } from '@angular/cdk/layout';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

export type BcLayout = 'phone' | 'tablet' | 'tabletLandscape' | 'desktop';

const QUERIES = {
  phone: '(max-width: 599.98px)',
  tablet: '(min-width: 600px) and (max-width: 1023.98px)',
  tabletLandscape: '(min-width: 1024px) and (max-width: 1279.98px)',
} as const;

/**
 * The one place that decides the layout for a screen width (docs/redesign/mobile/README.md). Use it
 * instead of reading `window.innerWidth`. The widths match _bc-breakpoints.scss.
 *
 * BreakpointObserver emits as soon as it is subscribed, so the first render already has the right
 * layout; 'desktop' is only the type-level default.
 */
@Injectable({ providedIn: 'root' })
export class BcLayoutService {
  private readonly breakpoints = inject(BreakpointObserver);

  readonly layout = toSignal(
    this.breakpoints.observe(Object.values(QUERIES)).pipe(map(state =>
      state.breakpoints[QUERIES.phone] ? 'phone'
        : state.breakpoints[QUERIES.tablet] ? 'tablet'
        : state.breakpoints[QUERIES.tabletLandscape] ? 'tabletLandscape'
        : 'desktop' as BcLayout)),
    { initialValue: 'desktop' as BcLayout },
  );

  readonly isPhone = computed(() => this.layout() === 'phone');
  /** Sidebar → 80px icon rail with labels, on both tablet layouts. */
  readonly useRail = computed(() => this.layout() === 'tablet' || this.layout() === 'tabletLandscape');
  /** Tables → card lists (phone and tablet portrait). */
  readonly useCards = computed(() => this.layout() === 'phone' || this.layout() === 'tablet');
  /** List and detail side by side (tablet landscape and up). */
  readonly splitView = computed(() => this.layout() === 'tabletLandscape' || this.layout() === 'desktop');

  /**
   * Set by a page that wants the whole phone screen (an open note thread, mockups/phone/MNote): the
   * shell hides its app bar and bottom tabs, and the page brings its own back button. The page
   * clears it when it closes. It only has an effect on phones.
   */
  readonly immersive = signal(false);
}
